<template>
  <div :class="b({ fullWidth })">
    <div :class="b('slider-wrapper')">
      <div
        ref="slider"
        :class="b('slider')"
        :style="sliderStyles"
      >
        <div
          ref="stage"
          :class="b('stage')"
          class="embla__container"
        >
          <!-- @slot Use this slot for the slide elements -->
          <slot :active="activeSlide"></slot>
        </div>
      </div>
    </div>

    <div
      v-if="showControls"
      :class="b('controls')"
    >
      <slot
        name="pagination"
        :count="noOfSlides"
        :active="activeSlide"
        :scroll-to="scrollTo"
      >
        <c-slide-pagination
          v-if="showPagination"
          :class="b('pagination')"
          :pages="noOfSlides"
          :active-page="activeSlide"
          @select="scrollTo"
        />
      </slot>

      <slot
        v-if="showNavigation"
        name="navigation"
        :previous="previous"
        :next="next"
        :disabled-prev="disablePrev"
        :disabled-next="disableNext"
      >
        <div
          v-if="defaultNavigation"
          :class="b('navigation')"
        >
          <e-icon-cta
            ref="previous"
            :class="b('navigation-button', { previous: true })"
            :disabled="disablePrev"
            :aria-label="$t('c-slider-2.previousLabel')"
            icon="i-navigate-previous"
            @click="previous"
          />
          <e-icon-cta
            ref="next"
            :class="b('navigation-button', { next: true })"
            :disabled="disableNext"
            :aria-label="$t('c-slider-2.nextLabel')"
            icon="i-navigate-next"
            @click="next"
          />
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
  import EmblaCarousel from 'embla-carousel';
  import cSlidePagination from '@/components/c-slide-pagination.vue';
  import eIconCta from '@/elements/e-icon-cta.vue';
  import mixinSliderInit from '@/mixins/slider-init.js';
  import CustomAutoplay from '@/plugins/embla-custom-autoplay/plugin.js';

  /**
   * Renders a 1-slide-per-view slider component.
   *
   */
  export default {
    name: 'c-slider-2',

    components: {
      eIconCta,
      cSlidePagination,
    },
    mixins: [mixinSliderInit],

    props: {
      /**
       * Enable pagination controls.
       */
      showPagination: {
        type: Boolean,
        default: false,
      },

      /**
       * Enable navigation controls.
       */
      showNavigation: {
        type: Boolean,
        default: true,
      },

      /**
       * Enable spanning across full container width.
       */
      fullWidth: {
        type: Boolean,
        default: false,
      },

      /**
       * Enable autoplay plugin.
       */
      autoplay: {
        type: Boolean,
        default: false,
      },

      /**
       * Enable looping if autoplay is enabled.
       */
      loop: {
        type: Boolean,
        default: true,
      },

      /**
       * Set fallback delay for slides without delay property set.
       */
      defaultDelay: {
        type: Number,
        default: 4000,
      },

      /**
       * Set the initial slide index.
       */
      startSlide: {
        type: Number,
        default: 0,
      },

      /**
       * Enable the default navigation arrows if now custom slot is used.
       */
      defaultNavigation: {
        type: Boolean,
        default: true,
      },
    },

    emits: ['change'],

    data() {
      return {
        /**
         * @type {EmblaCarousel} The related slider instance.
         */
        sliderInstance: null,

        /**
         * @type {Number} Holds the index of the active slide.
         */
        activeSlide: 0,

        /**
         * @type {Number} Holds the amount of total slides.
         */
        noOfSlides: 0,

        /**
         * @type {Boolean} Defines if the controls should be rendered or not.
         */
        showControls: false,

        /**
         * @type {Number} Holds the height of the current slide.
         */
        sliderHeight: null,
      };
    },

    computed: {
      /**
       * Defines if the previous button should be disabled.
       *
       * @returns {Boolean}
       */
      disablePrev() {
        return this.activeSlide === 0;
      },

      /**
       * Defines if the next button should be disabled.
       *
       * @returns {Boolean}
       */
      disableNext() {
        return this.activeSlide === this.noOfSlides - 1;
      },

      /**
       * Calculates inline style for the slider.
       *
       * @returns {Object}
       */
      sliderStyles() {
        return {
          height: this.sliderHeight ? `${this.sliderHeight}px` : null,
        };
      },
    },

    // watch: { },
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() { },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() { },
    // unmounted() {},

    methods: {
      /**
       * Returns Embla options based on the current viewport.
       *
       * @returns {Object}
       */
      getEmblaOptions() {
        return {
          startIndex: this.startSlide,
          inViewThreshold: 0.6, // The default of 0.5 resulted in invalid calculations on mobile.
        };
      },

      /**
       * Creates an instance of the Embla slider.
       */
      // NOTE: gets called via mixinSliderInit
      // eslint-disable-next-line vue/no-unused-properties
      createSlider() {
        if (this.sliderInstance) {
          return;
        }

        this.autoplayPlugin = this.autoplay
          ? new CustomAutoplay({
              stopOnLastSnap: !this.loop,
              defaultDelay: this.defaultDelay || 4000,
              delayFn: (embla) => {
                const slides = this.$slots.default?.()[0].children.filter((element) => element.tag);
                const child = slides[embla.selectedScrollSnap()];

                return child?.props['data-delay'];
              },
            })
          : null;

        const plugins = [this.autoplayPlugin].filter((plugin) => !!plugin);

        this.sliderInstance = new EmblaCarousel(this.$refs.slider, this.getEmblaOptions(), plugins);

        this.sliderInstance.on('init', this.onInit);
        this.sliderInstance.on('select', this.onSelect);
        this.sliderInstance.on('resize', this.onResize);

        // Since fonts have an impact on slide width and height, we need to update the slider when it finished loading.
        document.fonts?.ready.then(this.onFontReady);
      },

      /**
       * Callback for the fonts ready event.
       */
      onFontReady() {
        this.sliderInstance.reInit(this.getEmblaOptions());
      },

      /**
       * Event handler for the sliders init event.
       */
      onInit() {
        this.onResize();
        this.updateSlides();
      },

      /**
       * Event handler for the sliders select event.
       */
      onSelect() {
        this.updateSlides();
        this.updateHeight();
      },

      /**
       * Event handler for the resize event of the slider instance.
       */
      onResize() {
        this.updateSlides();
        this.updateHeight();
      },

      /**
       * Scrolls to the slide with the given index.
       *
       * @param {Number} slide - The desired slide index.
       */
      scrollTo(slide) {
        this.sliderInstance?.scrollTo(slide);
      },

      /**
       * Scrolls to the next slide.autoplayPlugin.
       */
      next() {
        this.autoplayPlugin?.interaction();
        this.scrollTo(this.activeSlide + 1);
      },

      /**
       * Scrolls to the previous slide.
       */
      previous() {
        this.autoplayPlugin?.interaction();
        this.scrollTo(this.activeSlide - 1);
      },

      /**
       * Pull state out of Embla instance.
       */
      updateSlides() {
        const slides = this.sliderInstance.slideNodes();

        this.activeSlide = this.sliderInstance.selectedScrollSnap();
        this.noOfSlides = slides.length;

        this.showControls = this.noOfSlides > 1;

        this.$emit('change', this.activeSlide);
      },

      /**
       * Updates the height of the slider to meet the active slides height.
       */
      updateHeight() {
        this.$nextTick(() => {
          // Makes sure the DOM updated before measuring.
          let height = null;
          const slides = this.sliderInstance.slideNodes();

          for (const slide of slides) {
            height = Math.max(height || 0, slide.offsetHeight);
          }

          this.sliderHeight = height > 0 ? height : null;
        });
      },

      /**
       * Reset and reinitialize slider instance.
       */
      // eslint-disable-next-line vue/no-unused-properties
      reset() {
        this.sliderInstance.reInit(this.getEmblaOptions());
        this.sliderInstance.scrollTo(0, true);
        this.updateSlides();
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';

  .c-slider-2 {
    $this: &;

    position: relative;

    @media print {
      break-inside: avoid;
    }

    &--full-width {
      @extend %fullwidth--content;

      #{$this}__navigation-button {
        display: none;

        @include mixins.media(md) {
          display: block;
        }

        &--previous {
          left: functions.rem(variables.$spacing--60);
        }

        &--next {
          right: functions.rem(variables.$spacing--60);
        }
      }
    }

    &__slider-wrapper {
      flex: 1 1 auto;
      overflow: hidden; // Prevents element from extending beyond flex container.
    }

    &__slider {
      transition: height variables.$transition-duration--200 ease;
    }

    &__stage {
      display: flex;
      justify-content: stretch;
    }

    &__stage > * {
      flex: 1 0 100%;
    }

    &__navigation-button {
      @include mixins.z-index(front);

      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      color: variables.$color-primary--2;
      transition: fill variables.$transition-duration--200;

      .e-icon-sprite {
        width: functions.rem(variables.$spacing--30);
        height: functions.rem(variables.$spacing--30);

        @include mixins.media(sm) {
          width: functions.rem(variables.$spacing--55);
          height: functions.rem(variables.$spacing--55);
        }
      }

      &:hover {
        color: variables.$color-primary--1;
      }

      &--previous {
        left: functions.rem(variables.$spacing--10);

        @include mixins.media(sm) {
          left: functions.rem(variables.$spacing--35);
        }

        @include mixins.media(md) {
          left: functions.rem(variables.$spacing--10);
        }
      }

      &--next {
        right: functions.rem(variables.$spacing--10);

        @include mixins.media(sm) {
          right: functions.rem(variables.$spacing--35);
        }

        @include mixins.media(md) {
          right: functions.rem(variables.$spacing--10);
        }
      }
    }

    &__pagination {
      @include mixins.z-index(front);

      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;

      @include mixins.media(sm) {
        padding-bottom: functions.rem(variables.$spacing--40);
      }
    }
  }
</style>
